import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './InstructorCourseDetailes.css';
import InstaTopbar from '../InstaTopbar/InstaTopbar';
import api from '../../api';
import { useAuth } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import {
  faCog,
  faTimes,
  faPen,
  faTrash,
  faDownload,
  faPlus,
  faUpload,
  faPaperclip,
  faClock,
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faPlay,
  faFileVideo,
  faWeight,
} from '@fortawesome/free-solid-svg-icons';
import { FaUpload } from 'react-icons/fa';
import swal from 'sweetalert';
import { FaHistory, FaMoneyBillAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';

const EditSectionModal = ({
  isOpen,
  onClose,
  onSave,
  sectionName,
  loading,
}) => {
  const [editedName, setEditedName] = useState(sectionName || '');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEditedName(sectionName || '');
      setHasChanges(false);
    }
  }, [isOpen, sectionName]);

  const handleNameChange = (e) => {
    setEditedName(e.target.value);
    setHasChanges(e.target.value !== sectionName);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && editedName.trim() && hasChanges && !loading) {
      e.preventDefault();
      onSave(editedName);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="in-modal-close-btn" onClick={onClose}>
          &times;
        </button>
        <h3>Edit Section</h3>
        <input
          type="text"
          value={editedName}
          onChange={handleNameChange}
          placeholder="Enter new section name"
          className="modal-input"
          onKeyDown={handleKeyDown}
        />
        <button
          className="add-course-save-btn"
          onClick={() => onSave(editedName)}
          disabled={loading || !hasChanges || !editedName.trim()}
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const AddSectionModal = ({ isOpen, onClose, onSave, loading }) => {
  const [sectionName, setSectionName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSectionName('');
      setHasChanges(false);
    }
  }, [isOpen]);

  const handleNameChange = (e) => {
    setSectionName(e.target.value);
    setHasChanges(e.target.value.trim() !== '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && sectionName.trim() && hasChanges && !loading) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleSave = () => {
    if (sectionName.trim() === '') {
      swal('Error!', 'Section name cannot be empty.', 'error');
      return;
    }
    onSave(sectionName);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="in-modal-close-btn" onClick={onClose}>
          &times;
        </button>
        <h3>Add New Section</h3>
        <input
          type="text"
          value={sectionName}
          onChange={handleNameChange}
          placeholder="Enter section name"
          className="modal-input"
          onKeyDown={handleKeyDown}
        />
        <button
          className="add-course-save-btn"
          onClick={handleSave}
          disabled={loading || !hasChanges || !sectionName.trim()}
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

const AddLessonModal = ({ isOpen, onClose, onSave, loading }) => {
  const [lessonName, setLessonName] = useState('');
  const [lessonVideo, setLessonVideo] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLessonName('');
      setLessonVideo(null);
      setFileInfo(null);
      setHasChanges(false);
    }
  }, [isOpen]);

  const handleNameChange = (e) => {
    setLessonName(e.target.value);
    setHasChanges(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && lessonName.trim() && hasChanges && !loading) {
      e.preventDefault();
      onSave(lessonName, lessonVideo);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleFileSelect = (file) => {
    if (file) {
      // Allowed video formats (MP4 is recommended - supports all devices, browsers, OS, and players)
      const allowedFormats = ['video/mp4', 'video/webm', 'video/avi', 'video/quicktime', 'video/x-msvideo'];
      const allowedExtensions = ['.mp4', '.webm', '.avi', '.mov'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      // Check if file is a video and if the type/extension is allowed
      const isValidFormat = allowedFormats.includes(file.type) || allowedExtensions.includes(fileExtension);
      if (!file.type.startsWith('video/') || !isValidFormat) {
        swal('Error!', 'Please upload a video file in MP4, AVI, MOV, or WebM format. MP4 is recommended for best compatibility.', 'error');
        return;
      }

      // Check file size (max 1.5GB for single lesson video)
      const maxSize = 1536 * 1024 * 1024; // 1.5GB in bytes
      if (file.size > maxSize) {
        swal('Error!', 'Video size should be less than 1.5GB for a single lesson. If your video is larger, please split it into multiple lessons.', 'error');
        return;
      }

      // Create a video element to check metadata
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        const quality = getVideoQuality(video.videoWidth, video.videoHeight); // Calculate quality

        // Check if the quality is below 720p
        if (video.videoHeight < 720) {
          swal('Warning!', 'The video quality must be at least 720p.', 'warning');
          return; // Exit if quality is not sufficient
        }

        // If quality is sufficient, set the lesson video and file info
        setLessonVideo(file);
        setHasChanges(true);
        setFileInfo({
          name: file.name,
          size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
          type: file.type,
          duration: 'Calculating...',
          quality: quality, // Set quality
        });

        // Get video duration
        const duration = video.duration;
        const minutes = Math.floor(duration / 60);
        const seconds = Math.floor(duration % 60);
        setFileInfo(prev => ({
          ...prev,
          duration: `${minutes}:${seconds.toString().padStart(2, '0')}`,
        }));
      };
      video.src = URL.createObjectURL(file);

      // Auto-fill lesson name from file name (without extension) if it's not already set
      if (!lessonName.trim()) {
        const nameWithoutExt = file.name.split('.').slice(0, -1).join('.');
        setLessonName(nameWithoutExt);
      }
    }
  };

  // Function to determine video quality based on width and height
  const getVideoQuality = (width, height) => {
    if (height >= 1440) return '1440p';
    if (height >= 1080) return '1080p';
    if (height >= 720) return '720p';
    if (height >= 480) return '480p';
    if (height >= 360) return '360p';
    return 'Unknown Quality';
  };

  const handleSave = () => {
    if (lessonName.trim() === '') {
      swal('Error!', 'Lesson name cannot be empty.', 'error');
      return;
    }
    if (!lessonVideo) {
      swal('Error!', 'Please upload a video for the lesson.', 'error');
      return;
    }
    onSave(lessonName, lessonVideo);
  };

  const handleClose = () => {
    setLessonName('');
    setLessonVideo(null);
    setFileInfo(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content add-lesson-modal" onClick={(e) => e.stopPropagation()}>
        <button className="in-modal-close-btn" onClick={handleClose}>
          &times;
        </button>
        <h3>Add New Lesson</h3>

        <div className="lesson-form">
          <input
            type="text"
            value={lessonName}
            onChange={handleNameChange}
            placeholder="Enter lesson name"
            className="modal-input"
            onKeyDown={handleKeyDown}
          />

          <div
            className={`video-upload-container ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="video/*"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              className="video-input"
              id="video-upload"
            />
            <label htmlFor="video-upload" className="video-upload-label">
              <FontAwesomeIcon icon={faUpload} className="upload-icon" />
              <p>Drag and drop your video here or click to browse</p>
              <span className="upload-info">
                Supported formats: MP4, AVI, MOV, WebM<br />
                <strong>Recommended: MP4</strong> (best compatibility)<br />
                Maximum size per lesson: 1.5GB<br />
                Maximum total course size: 2GB
              </span>
            </label>
          </div>

          {fileInfo && (
            <div className="file-info">
              <div className="file-info-header">
                <FontAwesomeIcon icon={faPlay} className="file-icon" />
                <span className="file-name">{fileInfo.name}</span>
              </div>
              <div className="file-details">
                <div className="detail-item">
                  <FontAwesomeIcon icon={faFileVideo} />
                  <span>Type: {fileInfo.type.split('/')[1].toUpperCase()}</span>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faWeight} />
                  <span>Size: {fileInfo.size}</span>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faClock} />
                  <span>Duration: {fileInfo.duration}</span>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faCog} />
                  <span>Quality: {fileInfo.quality}</span> {/* Display quality */}
                </div>
              </div>
            </div>
          )}

          <button
            className={`add-course-save-btn ${loading ? 'loading' : ''}`}
            onClick={handleSave}
            disabled={loading || !hasChanges || !lessonVideo || !lessonName.trim()}
          >
            {loading ? 'Uploading...' : 'Save Lesson'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditLessonModal = ({ isOpen, onClose, onSave, lessonName, loading }) => {
  const [editedName, setEditedName] = useState(lessonName);
  const [editedVideo, setEditedVideo] = useState(null);
  const [editType, setEditType] = useState('name'); // 'name', 'video', or 'both'
  const [fileInfo, setFileInfo] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEditedName(lessonName);
      setEditedVideo(null);
      setFileInfo(null);
      setHasChanges(false);
    }
  }, [isOpen, lessonName]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleFileSelect = (file) => {
    if (file) {
      // Allowed video formats (MP4 is recommended - supports all devices, browsers, OS, and players)
      const allowedFormats = ['video/mp4', 'video/webm', 'video/avi', 'video/quicktime', 'video/x-msvideo'];
      const allowedExtensions = ['.mp4', '.webm', '.avi', '.mov'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      // Check if file is a video and if the type/extension is allowed
      const isValidFormat = allowedFormats.includes(file.type) || allowedExtensions.includes(fileExtension);
      if (!file.type.startsWith('video/') || !isValidFormat) {
        swal('Error!', 'Please upload a video file in MP4, AVI, MOV, or WebM format. MP4 is recommended for best compatibility.', 'error');
        return;
      }

      // Check file size (max 1.5GB for single lesson video)
      const maxSize = 1536 * 1024 * 1024; // 1.5GB in bytes
      if (file.size > maxSize) {
        swal('Error!', 'Video size should be less than 1.5GB for a single lesson. If your video is larger, please split it into multiple lessons.', 'error');
        return;
      }

      // Create a video element to check metadata
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = function () {
        window.URL.revokeObjectURL(video.src);
        const quality = getVideoQuality(video.videoWidth, video.videoHeight); // Calculate quality

        // Check if the quality is below 720p
        if (video.videoHeight < 720) {
          swal('Warning!', 'The video quality must be at least 720p.', 'warning');
          return; // Exit if quality is not sufficient
        }

        // If quality is sufficient, set the edited video and file info
        setEditedVideo(file);
        setHasChanges(true);
        setFileInfo({
          name: file.name,
          size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
          type: file.type,
          duration: 'Calculating...',
          quality: quality,
        });

        // Get video duration
        const duration = video.duration;
        const minutes = Math.floor(duration / 60);
        const seconds = Math.floor(duration % 60);
        setFileInfo(prev => ({
          ...prev,
          duration: `${minutes}:${seconds.toString().padStart(2, '0')}`,
        }));
      };
      video.src = URL.createObjectURL(file);
    }
  };

  // Function to determine video quality based on width and height
  const getVideoQuality = (width, height) => {
    if (height >= 1440) return '1440p';
    if (height >= 1080) return '1080p';
    if (height >= 720) return '720p';
    if (height >= 480) return '480p';
    if (height >= 360) return '360p';
    return 'Unknown Quality';
  };

  const handleSave = () => {
    if ((editType === 'name' || editType === 'both') && !editedName.trim()) {
      swal('Error!', 'Lesson name cannot be empty.', 'error');
      return;
    }
    if ((editType === 'video' || editType === 'both') && !editedVideo) {
      swal('Error!', 'Please upload a video for the lesson.', 'error');
      return;
    }
    onSave(editType, editedName, editedVideo);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && hasChanges && !loading) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleClose = () => {
    setEditedName(lessonName);
    setEditedVideo(null);
    setFileInfo(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content add-lesson-modal" onClick={(e) => e.stopPropagation()}>
        <button className="in-modal-close-btn" onClick={handleClose}>
          &times;
        </button>
        <h3>Edit Lesson</h3>

        <div className="lesson-form">
          <label>Select Edit Type:</label>
          <select
            value={editType}
            onChange={(e) => setEditType(e.target.value)}
            className="modal-input"
          >
            <option value="name">Edit Name</option>
            <option value="video">Edit Video</option>
            <option value="both">Edit Both</option>
          </select>

          {(editType === 'name' || editType === 'both') && (
            <input
              type="text"
              value={editedName}
              onChange={(e) => {
                setEditedName(e.target.value);
                setHasChanges(true);
              }}
              placeholder="Enter new lesson name"
              className="modal-input"
              onKeyDown={handleKeyDown}
            />
          )}

          {(editType === 'video' || editType === 'both') && (
            <div
              className={`video-upload-container ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept="video/*"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="video-input"
                id="video-upload-edit"
              />
              <label htmlFor="video-upload-edit" className="video-upload-label">
                <FontAwesomeIcon icon={faUpload} className="upload-icon" />
                <p>Drag and drop your video here or click to browse</p>
                <span className="upload-info">
                  Supported formats: MP4, AVI, MOV, WebM<br />
                  <strong>Recommended: MP4</strong> (best compatibility)<br />
                  Maximum size per lesson: 1.5GB<br />
                  Maximum total course size: 2GB
                </span>
              </label>
            </div>
          )}

          {fileInfo && (editType === 'video' || editType === 'both') && (
            <div className="file-info">
              <div className="file-info-header">
                <FontAwesomeIcon icon={faPlay} className="file-icon" />
                <span className="file-name">{fileInfo.name}</span>
              </div>
              <div className="file-details">
                <div className="detail-item">
                  <FontAwesomeIcon icon={faFileVideo} />
                  <span>Type: {fileInfo.type.split('/')[1].toUpperCase()}</span>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faWeight} />
                  <span>Size: {fileInfo.size}</span>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faClock} />
                  <span>Duration: {fileInfo.duration}</span>
                </div>
                <div className="detail-item">
                  <FontAwesomeIcon icon={faCog} />
                  <span>Quality: {fileInfo.quality}</span>
                </div>
              </div>
            </div>
          )}

          <button
            className={`add-course-save-btn ${loading ? 'loading' : ''}`}
            onClick={handleSave}
            disabled={loading || !hasChanges || (editType === 'video' && !editedVideo) || (editType === 'name' && !editedName.trim())}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MediaModal = ({ isOpen, onClose, content }) => {
  const videoRef = React.useRef(null);

  const handleClose = () => {
    if (content.type === 'video' && videoRef.current) {
      videoRef.current.pause(); // Pause the video when the modal is closed
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="in-video-close-btn" onClick={handleClose}>
          &times;
        </button>
        {content.type === 'image' && (
          <img src={content.src} alt="Enlarged view" className="modal-media" />
        )}
        {content.type === 'video' && (
          <video ref={videoRef} className="modal-media" controls>
            <source src={content.src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>
    </div>
  );
};

const AddAttachmentModal = ({ isOpen, onClose, onSave, loading }) => {
  const [attachmentName, setAttachmentName] = useState('');
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedAttachment(file);
      setFileInfo({
        name: file.name,
        size: (file.size / 1024 / 1024).toFixed(2) + ' MB',
        type: file.type || 'Unknown',
      });
      // Auto-fill attachment name from file name
      setAttachmentName(file.name.split('.').slice(0, -1).join('.'));
    }
  };

  const handleSave = () => {
    if (attachmentName.trim() === '') {
      swal('Error!', 'Attachment name cannot be empty.', 'error');
      return;
    }
    if (!selectedAttachment) {
      swal('Error!', 'Please select a file.', 'error');
      return;
    }
    onSave(attachmentName, selectedAttachment);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && attachmentName.trim() && selectedAttachment && !loading) {
      e.preventDefault();
      handleSave();
    }
  };

  const handleClose = () => {
    setAttachmentName('');
    setSelectedAttachment(null);
    setFileInfo(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="add-attachment-modal" onClick={e => e.stopPropagation()}>
        <button className="in-modal-close-btn" onClick={handleClose}>
          &times;
        </button>
        <h3>Add Attachment</h3>
        <input
          type="text"
          value={attachmentName}
          onChange={e => setAttachmentName(e.target.value)}
          placeholder="Enter attachment name"
          className="modal-input"
          onKeyDown={handleKeyDown}
        />
        <div
          className="file-upload-container"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          style={{ cursor: 'pointer' }}
        >
          <input
            type="file"
            accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.zip,.rar"
            onChange={handleFileChange}
            id="file-upload"
            ref={fileInputRef}
            style={{ display: 'none' }}
          />
          <p>Click to select or drag and drop your file here</p>
          <p style={{ fontSize: '12px', color: '#6a6f73' }}>
            Supported formats: PDF, DOC, DOCX, PPT, PPTX, TXT, ZIP, RAR
          </p>
        </div>
        {fileInfo && (
          <div className="file-info">
            <p>
              <FontAwesomeIcon icon={faPaperclip} />
              File: {fileInfo.name}
            </p>
            <p>
              <FontAwesomeIcon icon={faDownload} />
              Size: {fileInfo.size}
            </p>
            <p>
              <FontAwesomeIcon icon={faCog} />
              Type: {fileInfo.type}
            </p>
          </div>
        )}
        <button
          className={`add-course-save-btn ${loading ? 'loading' : ''}`}
          onClick={handleSave}
          disabled={loading || !selectedAttachment || !attachmentName.trim()}
        >
          {loading ? '' : 'Upload Attachment'}
        </button>
      </div>
    </div>
  );
};

const InstructorCourseDetails = () => {
  const { token } = useAuth();
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isOverviewOpen, setIsOverviewOpen] = useState(false);
  const [editOverview, setEditOverview] = useState(false);
  const [overviewFields, setOverviewFields] = useState({
    detailedDescription: '',
    requirements: [''],
    youLearner: [''],
    targetAudience: [''],
    language: '',
  });
  const [isDraft, setIsDraft] = useState(false);
  const [courseId, setCourseId] = useState(null);
  const [isEditLessonModalOpen, setIsEditLessonModalOpen] = useState(false);
  const [currentLessonId, setCurrentLessonId] = useState(null);
  const [currentLessonName, setCurrentLessonName] = useState('');
  const [isAddLessonModalOpen, setIsAddLessonModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditSectionModalOpen, setIsEditSectionModalOpen] = useState(false);
  const [editLessonName, setEditLessonName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [isAddSectionModalOpen, setIsAddSectionModalOpen] = useState(false);
  const [expandedSectionId, setExpandedSectionId] = useState(null);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [currentSectionName, setCurrentSectionName] = useState('');
  const [expandedLessonId, setExpandedLessonId] = useState(null);
  const [attachmentName, setAttachmentName] = useState('');
  const [expandedAttachments, setExpandedAttachments] = useState({});
  const [isAddAttachmentModalOpen, setIsAddAttachmentModalOpen] = useState(false);
  const [currentAttachmentLessonId, setCurrentAttachmentLessonId] = useState(null);
  const [currentAttachmentSectionId, setCurrentAttachmentSectionId] = useState(null);
  const [hasSettingsChanges, setHasSettingsChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState({
    description: '',
    video: null,
    image: null,
  });
  const totalHours = Math.floor(course?.totalHours || 0); // Get whole hours
  const totalMinutes = Math.round((course?.totalHours || 0) * 60) % 60; // Convert remaining part to minutes
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [expandedVideos, setExpandedVideos] = useState({});
  const videoRefs = useRef({}); // Add this to store video element references
  const [hasOverviewChanges, setHasOverviewChanges] = useState(false); // New state for tracking overview changes
  const [originalOverviewFields, setOriginalOverviewFields] = useState({}); // New state for original overview fields
  const [criteriaValues, setCriteriaValues] = useState({ actualValue: 1.5, offerValue: 1 });
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [isFree, setIsFree] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    actualPrice: '',
    offerPrice: '',
    currency: 'INR',
  });
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [draftFields, setDraftFields] = useState({
    title: false,
    description: false,
    detailedDescription: false,
    requirements: false,
    youLearner: false,
    instructor: false,
    instructorProfilePicture: false,
    category: false,
    subcategory: false,
    video: false,
    image: false,
    sections: false,
    actualPrice: false,
    offerPrice: false,
    currency: false,
    isFree: false,
    language: false,
    targetAudience: false,
  });
  const [lastEditedOverviewField, setLastEditedOverviewField] = useState(null);
  const [initialPaymentDetails, setInitialPaymentDetails] = useState({
    actualPrice: '',
    offerPrice: '',
    currency: 'INR',
    isFree: false,
  });

  const navigate = useNavigate();
  useBlockCheck('instructor');

  // TEMPORARY PERIOD: platform is offering all courses as FREE at the start.
  // Set this to `false` when you want instructors to manage pricing normally.
  const PLATFORM_TEMPORARY_FREE_COURSES = true;

  const effectiveIsFree = PLATFORM_TEMPORARY_FREE_COURSES ? true : isFree;
  const lockPricingFields = PLATFORM_TEMPORARY_FREE_COURSES || effectiveIsFree;

  const toggleAttachment = (lessonId, documentId) => {
    setExpandedAttachments((prev) => ({
      ...prev,
      [lessonId]: {
        ...prev[lessonId],
        [documentId]: !prev[lessonId]?.[documentId],
      },
    }));
  };

  const handleLessonClick = (lessonId) => {
    // If closing the lesson, also close its video
    if (expandedLessonId === lessonId) {
      // Stop and hide video if it's playing
      if (expandedVideos[lessonId]) {
        if (videoRefs.current[lessonId]) {
          videoRefs.current[lessonId].pause();
        }
        setExpandedVideos(prev => ({
          ...prev,
          [lessonId]: false,
        }));
      }
      setExpandedLessonId(null);
    } else {
      // Expand lesson and open video by default
      setExpandedLessonId(lessonId);
      setExpandedVideos(prev => ({
        ...prev,
        [lessonId]: true,
      }));
    }
  };

  const toggleVideo = (lessonId) => {
    setExpandedVideos(prev => {
      const newState = {
        ...prev,
        [lessonId]: !prev[lessonId],
      };

      // If we're closing the video, pause it
      if (!newState[lessonId] && videoRefs.current[lessonId]) {
        videoRefs.current[lessonId].pause();
      }

      return newState;
    });
  };

  useEffect(() => {
    const fetchCourse = async () => {
      setLoading(true);
      try {
        const draftRes = await axios.get(api.getCourseDraftById(id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (draftRes.data && draftRes.data.draft) {
          setCourse(draftRes.data.draft);
          setIsDraft(true);
          setCourseId(draftRes.data.draft._id);
        } else {
          // fallback to published
          const courseRes = await axios.get(api.getCourseById(id), {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (courseRes.data && courseRes.data.course) {
            setCourse(courseRes.data.course);
            setIsDraft(false);
            setCourseId(courseRes.data.course._id);
          } else {
            navigate('/my-courses');
          }
        }
      } catch (error) {
        console.error('Failed to fetch course:', error);
        navigate('/my-courses');
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id, token, navigate]);

  useEffect(() => {
    const fetchCriteria = async () => {
      try {
        const res = await axios.get(api.addValue, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data) {
          setCriteriaValues({
            actualValue: res.data.actualValue || 1.5,
            offerValue: res.data.offerValue || 1,
          });
        }
      } catch (err) {
        console.error('Failed to fetch criteria defaults:', err);
      }
    };

    fetchCriteria();
  }, [token]);

  useEffect(() => {
    if (course) {
      const initialOverviewFields = {
        detailedDescription: course.detailedDescription || '',
        requirements: course.requirements && course.requirements.length ? course.requirements : [''],
        youLearner: course.youLearner && course.youLearner.length ? course.youLearner : [''],
        targetAudience: course.targetAudience && course.targetAudience.length ? course.targetAudience : [''],
        language: course.language || '',
      };
      setOverviewFields(initialOverviewFields);
      setOriginalOverviewFields(initialOverviewFields); // Set original fields
      setHasOverviewChanges(false); // Reset changes when course is fetched
    }
  }, [course]);

  // Update handleOverviewFieldChange to set draft status to true when changes are made
  const handleOverviewFieldChange = (field, value) => {
    setOverviewFields(prev => {
      const updatedFields = { ...prev, [field]: value };
      // Check if updated fields differ from original fields
      const isChanged = JSON.stringify(updatedFields) !== JSON.stringify(originalOverviewFields);
      setHasOverviewChanges(isChanged);
      setLastEditedOverviewField(field); // Track last edited field
      // Set draft status to true only if there are actual changes
      if (isChanged) {
        setDraftFields(prev => ({
          ...prev,
          [field]: true,
        }));
      }
      return updatedFields;
    });
  };

  // Update handleSaveOverview to save only the last edited field
  const handleSaveOverview = async (e) => {
    e.preventDefault();
    if (!lastEditedOverviewField) return;
    setLoading(true);
    try {
      let payload = { draftFields: {} };
      if (lastEditedOverviewField === 'detailedDescription') {
        payload.detailedDescription = overviewFields.detailedDescription;
      } else if (lastEditedOverviewField === 'requirements') {
        payload.requirements = JSON.stringify(overviewFields.requirements.filter(r => r.trim()));
      } else if (lastEditedOverviewField === 'youLearner') {
        payload.youLearner = JSON.stringify(overviewFields.youLearner.filter(y => y.trim()));
      } else if (lastEditedOverviewField === 'targetAudience') {
        payload.targetAudience = JSON.stringify(overviewFields.targetAudience.filter(t => t.trim()));
      } else if (lastEditedOverviewField === 'language') {
        payload.language = overviewFields.language;
      }
      payload.draftFields[lastEditedOverviewField] = true;
      const response = await axios.put(api.updateCourseDraft(id), payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCourse(response.data.draft);
      setEditOverview(false);
      setOriginalOverviewFields(overviewFields);
      setHasOverviewChanges(false);
      setLastEditedOverviewField(null);
      // Keep draft status true for saved field
      setDraftFields(prev => ({
        ...prev,
        [lastEditedOverviewField]: true,
      }));
      swal('Success!', 'Overview updated!', 'success');
    } catch (error) {
      swal('Error!', 'Failed to update overview.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPaymentModal = async (courseId) => {
    setSelectedCourseId(courseId);
    // During temporary period, always force FREE
    if (PLATFORM_TEMPORARY_FREE_COURSES) setIsFree(true);

    try {
      let draftData = null;
      
      // Check if course is published - if so, get the draftId and fetch draft
      if (course?.published && course?.draftId) {
        // Course is published, fetch the draft using draftId
        const response = await axios.get(api.getCourseDraftById(course.draftId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        draftData = response.data?.draft;
      } else {
        // Course is still a draft, fetch using courseId
        const response = await axios.get(api.getCourseDraftById(courseId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        draftData = response.data?.draft;
      }

      // Fallback to current course data if draft fetch fails
      const courseData = draftData || course;
      if (!courseData) throw new Error('Course not found');

      const totalHours = courseData.totalHours || 0;

      const defaultActual = Math.round(totalHours * 60 * criteriaValues.actualValue);
      const defaultOffer = Math.round(totalHours * 60 * criteriaValues.offerValue);

      const loadedDetails = {
        actualPrice: courseData.actualPrice || defaultActual,
        offerPrice: PLATFORM_TEMPORARY_FREE_COURSES ? 0 : (courseData.offerPrice || defaultOffer),
        currency: courseData.currency || 'INR',
        isFree: PLATFORM_TEMPORARY_FREE_COURSES ? true : (courseData.isFree || false),
      };
      setPaymentDetails({
        actualPrice: loadedDetails.actualPrice,
        offerPrice: loadedDetails.offerPrice,
        currency: loadedDetails.currency,
      });
      setIsFree(loadedDetails.isFree);
      setInitialPaymentDetails(loadedDetails); // Track initial values
      setShowPaymentModal(true);
    } catch (error) {
      console.error('Failed to fetch course payment details', error);
      // Fallback: use current course data if API call fails
      if (course) {
        const totalHours = course.totalHours || 0;
        const defaultActual = Math.round(totalHours * 60 * criteriaValues.actualValue);
        const defaultOffer = Math.round(totalHours * 60 * criteriaValues.offerValue);
        
        const loadedDetails = {
          actualPrice: course.actualPrice || defaultActual,
          offerPrice: PLATFORM_TEMPORARY_FREE_COURSES ? 0 : (course.offerPrice || defaultOffer),
          currency: course.currency || 'INR',
          isFree: PLATFORM_TEMPORARY_FREE_COURSES ? true : (course.isFree || false),
        };
        setPaymentDetails({
          actualPrice: loadedDetails.actualPrice,
          offerPrice: loadedDetails.offerPrice,
          currency: loadedDetails.currency,
        });
        setIsFree(loadedDetails.isFree);
        setInitialPaymentDetails(loadedDetails);
        setShowPaymentModal(true);
      } else {
        swal('Error', 'Failed to load payment info.', 'error');
      }
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedCourseId(null);
    if (PLATFORM_TEMPORARY_FREE_COURSES) setIsFree(true);
    setPaymentDetails({ actualPrice: '', offerPrice: '', currency: 'INR' });
  };

  // Track changes to payment details and isFree
  useEffect(() => {
    const changed =
      String(paymentDetails.actualPrice) !== String(initialPaymentDetails.actualPrice) ||
      String(paymentDetails.offerPrice) !== String(initialPaymentDetails.offerPrice) ||
      paymentDetails.currency !== initialPaymentDetails.currency ||
      isFree !== initialPaymentDetails.isFree;
    setHasChanges(changed);
  }, [paymentDetails, isFree, initialPaymentDetails]);

  // Update handleSubmitPayment to set draft status to true
  const handleSubmitPayment = async () => {
    const { actualPrice } = paymentDetails;

    // When pricing is locked / free, instructors can't edit pricing; don't block save.
    if (!lockPricingFields && !actualPrice) {
      swal('Missing Info', 'Please enter Actual Price.', 'warning');
      return;
    }

    // Determine the draft ID to use
    // If course is published, use course.draftId, otherwise use selectedCourseId
    const draftIdToUse = (course?.published && course?.draftId) 
      ? course.draftId 
      : selectedCourseId;

    const payload = {
      ...paymentDetails,
      offerPrice: effectiveIsFree ? 0 : paymentDetails.offerPrice,
      isFree: effectiveIsFree,
      draftFields: {
        actualPrice: true,
        offerPrice: true,
        currency: true,
        isFree: true,
      },
    };

    try {
      const response = await axios.post(api.addDraftCoursePayment(draftIdToUse), payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Update course state immediately with the updated draft data
      if (response.data && response.data.draft) {
        // If course is published, update the draft but keep the published course structure
        if (course?.published && course?.draftId) {
          // Update draftFields in the current course state to reflect the changes
          setCourse(prev => ({
            ...prev,
            draftFields: {
              ...(prev.draftFields || {}),
              actualPrice: true,
              offerPrice: true,
              currency: true,
              isFree: true,
            },
            actualPrice: response.data.draft.actualPrice,
            offerPrice: response.data.draft.offerPrice,
            currency: response.data.draft.currency,
            isFree: response.data.draft.isFree,
          }));
        } else {
          // Course is still a draft, update with draft data
          setCourse(response.data.draft);
        }
      }
      
      swal('Success', 'Payment info added successfully!', 'success');
      handleClosePaymentModal();
    } catch (error) {
      swal('Error', 'Failed to add payment info.', 'error');
    }
  };

  const handleSetDefaultPrice = () => {
    const totalHours = course?.totalHours || 0;
    const defaultActual = Math.round(totalHours * 60 * criteriaValues.actualValue);
    const defaultOffer = Math.round(totalHours * 60 * criteriaValues.offerValue);

    setPaymentDetails((prev) => ({
      ...prev,
      actualPrice: defaultActual,
      offerPrice: PLATFORM_TEMPORARY_FREE_COURSES ? 0 : defaultOffer,
    }));
    setIsFree(PLATFORM_TEMPORARY_FREE_COURSES ? true : false);
  };




  const toggleOverview = () => setIsOverviewOpen((prev) => !prev);

  // Add this useEffect near the top of the component, after all the state declarations
  useEffect(() => {
    // This is a workaround for the ResizeObserver loop error
    const resizeObserverError = error => {
      if (error.message.includes('ResizeObserver')) {
        const resizeObserverErr = document.getElementById('webpack-dev-server-client-overlay');
        if (resizeObserverErr) {
          resizeObserverErr.style.display = 'none';
        }
      }
    };

    window.addEventListener('error', resizeObserverError);

    return () => {
      window.removeEventListener('error', resizeObserverError);
    };
  }, []);

  useEffect(() => {
    if (course) {
      setOriginalSettings({
        description: course.description || '',
        video: course.video,
        image: course.image,
      });
    }
  }, [course]);

  const handleSettingsChange = (field, value) => {
    setCourse(prev => ({
      ...prev,
      [field]: value,
    }));

    // Check if any changes have been made
    const hasChanges =
      (field === 'description' && value !== originalSettings.description) ||
      (field === 'video' && value !== originalSettings.video) ||
      (field === 'image' && value !== originalSettings.image) ||
      selectedVideo !== null ||
      selectedAttachment !== null;

    setHasSettingsChanges(hasChanges);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading course details...</p>
      </div>
    );
  }
  // console.log(course)
  if (!course) {
    return (
      <div className="loading-container">
        <p className="loading-text">Course not found or failed to load.</p>
      </div>
    );
  }


  const toggleSettings = () => {
    setIsSettingsOpen((prev) => !prev);
  };

  const openModal = (content) => {
    setModalContent(content);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent(null);
  };

  // --- DRAFT SECTION/LESSON/ATTACHMENT HANDLERS ---
  const handleAddSection = async (sectionName) => {
    try {
      setLoading(true);
      const response = await axios.post(
        api.addDraftSection(id),
        { sectionName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setCourse(response.data.draft);
      setHasChanges(true);
      swal('Success!', 'Section added successfully!', 'success');
    } catch (error) {
      swal('Error!', 'Failed to add section. Please try again.', 'error');
      console.error('Error adding section:', error);
    } finally {
      setLoading(false);
      setIsAddSectionModalOpen(false);
    }
  };

  const handleEditSection = async (editedName) => {
    try {
      setLoading(true);
      const response = await axios.put(
        api.editDraftSection(id, currentSectionId),
        { sectionName: editedName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setCourse(response.data.draft);
      swal('Success!', 'Section updated successfully!', 'success');
    } catch (error) {
      swal('Error!', 'Failed to update section. Please try again.', 'error');
      console.error('Error editing section:', error);
    } finally {
      setLoading(false);
      setIsEditSectionModalOpen(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    swal({
      title: 'Are you sure?',
      text: 'Once deleted, you will not be able to recover this section!',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          const response = await axios.delete(
            api.deleteDraftSection(id, sectionId),
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          setCourse(response.data.draft);
          setHasChanges(true); // Enable the Update button
          swal('Deleted!', 'Section deleted successfully!', 'success');
        } catch (error) {
          swal('Error!', 'Failed to delete section. Please try again.', 'error');
          console.error('Error deleting section:', error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleAddLesson = async (lessonName, lessonVideo) => {
    const formData = new FormData();
    formData.append('lessonName', lessonName);
    formData.append('video', lessonVideo);
    
    // Calculate video size for error messages
    const videoSizeMB = lessonVideo ? (lessonVideo.size / 1024 / 1024).toFixed(2) : 0;
    const videoSizeBytes = lessonVideo ? lessonVideo.size : 0;
    
    // Check if single video exceeds 1.5GB
    const MAX_SINGLE_VIDEO_SIZE = 1536 * 1024 * 1024; // 1.5GB
    if (videoSizeBytes > MAX_SINGLE_VIDEO_SIZE) {
      swal({
        icon: 'error',
        title: 'File Too Large',
        html: `The video file is too large (${videoSizeMB} MB).<br/><br/>
               <strong style="color: red;">⚠️ Single lesson video cannot exceed 1.5GB!</strong><br/>
               Please split your video into multiple lessons or compress it.`,
      });
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('📤 Uploading lesson video:', {
        lessonName,
        videoSize: `${videoSizeMB} MB`,
        videoType: lessonVideo?.type,
      });
      
      const response = await axios.post(
        api.addDraftLesson(id, currentSectionId),
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          maxContentLength: 1024 * 1024 * 1024 * 2, // 2GB max content length
          maxBodyLength: 1024 * 1024 * 1024 * 2, // 2GB max body length
          timeout: 300000, // 5 minutes timeout for large file uploads
        },
      );
      setCourse(response.data.draft);
      setHasChanges(true);
      swal('Success!', 'Lesson added successfully!', 'success');
    } catch (error) {
      console.error('Error adding lesson:', error);
      
      // Handle specific error cases
      if (error.response?.status === 413) {
        let errorMessage = `The video file is too large (${videoSizeMB} MB).<br/><br/>`;
        if (lessonVideo && lessonVideo.size > 1536 * 1024 * 1024) {
          errorMessage += `<strong style="color: red;">⚠️ Your video exceeds the 1.5GB limit for a single lesson!</strong><br/>`;
          errorMessage += `Please split your video into multiple lessons or compress it.<br/><br/>`;
        } else {
          errorMessage += `<strong>Maximum allowed size per lesson: 1.5GB</strong><br/>`;
          errorMessage += `<strong>Maximum total course size: 2GB</strong><br/>`;
          errorMessage += `This might be a server configuration issue. Please try:<br/>`;
          errorMessage += `• Compressing the video file<br/>`;
          errorMessage += `• Splitting into multiple lessons<br/>`;
          errorMessage += `• Contacting support if the file is under 1.5GB`;
        }
        swal({
          icon: 'error',
          title: 'File Too Large',
          html: errorMessage,
        });
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        swal('Error!', 'Upload timeout. The video file may be too large. Please try with a smaller file.', 'error');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to add lesson. Please try again.';
        swal('Error!', errorMessage, 'error');
      }
    } finally {
      setLoading(false);
      setIsAddLessonModalOpen(false);
    }
  };

  const handleEditLesson = async (sectionId, lessonId, editType, editedName, editedVideo) => {
    const formData = new FormData();
    if (editType === 'name' || editType === 'both') {
      formData.append('lessonName', editedName);
    } else {
      formData.append('lessonName', editedName || currentLessonName);
    }
    if (editType === 'video' || editType === 'both') {
      formData.append('video', editedVideo);
    }
    
    // Calculate video size for error messages
    const videoSizeMB = editedVideo ? (editedVideo.size / 1024 / 1024).toFixed(2) : 0;
    const videoSizeBytes = editedVideo ? editedVideo.size : 0;
    
    // Check if single video exceeds 1.5GB
    if (editedVideo) {
      const MAX_SINGLE_VIDEO_SIZE = 1536 * 1024 * 1024; // 1.5GB
      if (videoSizeBytes > MAX_SINGLE_VIDEO_SIZE) {
        swal({
          icon: 'error',
          title: 'File Too Large',
          html: `The video file is too large (${videoSizeMB} MB).<br/><br/>
                 <strong style="color: red;">⚠️ Single lesson video cannot exceed 1.5GB!</strong><br/>
                 Please split your video into multiple lessons or compress it.`,
        });
        return;
      }
    }
    
    try {
      setLoading(true);
      
      if (editedVideo) {
        console.log('📤 Uploading lesson video update:', {
          lessonName: editedName || currentLessonName,
          videoSize: `${videoSizeMB} MB`,
          videoType: editedVideo?.type,
        });
      }
      
      const response = await axios.put(
        api.editDraftLesson(id, sectionId, lessonId),
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
          maxContentLength: 1024 * 1024 * 1024 * 2, // 2GB max content length
          maxBodyLength: 1024 * 1024 * 1024 * 2, // 2GB max body length
          timeout: 300000, // 5 minutes timeout for large file uploads
        },
      );
      if (response.status === 200) {
        setCourse(response.data.draft);
        swal('Success!', 'Lesson updated successfully!', 'success');
      } else {
        swal('Error!', 'Failed to update lesson.', 'error');
      }
    } catch (error) {
      console.error('Error editing lesson:', error);
      
      // Handle specific error cases
      if (error.response?.status === 413) {
        let errorMessage = `The video file is too large (${videoSizeMB} MB).<br/><br/>`;
        if (editedVideo && editedVideo.size > 1536 * 1024 * 1024) {
          errorMessage += `<strong style="color: red;">⚠️ Your video exceeds the 1.5GB limit for a single lesson!</strong><br/>`;
          errorMessage += `Please split your video into multiple lessons or compress it.`;
        } else {
          errorMessage += `<strong>Maximum allowed size per lesson: 1.5GB</strong><br/>`;
          errorMessage += `<strong>Maximum total course size: 2GB</strong><br/>`;
          errorMessage += `Please try compressing the video or splitting into multiple lessons.`;
        }
        swal({
          icon: 'error',
          title: 'File Too Large',
          html: errorMessage,
        });
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        swal('Error!', 'Upload timeout. The video file may be too large. Please try with a smaller file.', 'error');
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to update lesson. Please try again.';
        swal('Error!', errorMessage, 'error');
      }
    } finally {
      setLoading(false);
      setIsEditLessonModalOpen(false);
    }
  };

  const handleDeleteLesson = async (sectionId, lessonId) => {
    swal({
      title: 'Are you sure?',
      text: 'Once deleted, you will not be able to recover this lesson!',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          const response = await axios.delete(
            api.deleteDraftLesson(id, sectionId, lessonId),
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          setCourse(response.data.draft);
          setHasChanges(true); // Enable the Update button
          swal('Deleted!', 'Lesson deleted successfully!', 'success');
        } catch (error) {
          swal('Error!', 'Failed to delete lesson. Please try again.', 'error');
          console.error('Error deleting lesson:', error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleAddAttachment = async (attachmentName, selectedAttachment) => {
    if (!currentAttachmentSectionId || !currentAttachmentLessonId) {
      swal('Error!', 'Section or lesson information is missing.', 'error');
      return;
    }
    const formData = new FormData();
    formData.append('documentName', attachmentName);
    formData.append('attachment', selectedAttachment);
    try {
      setLoading(true);
      const response = await axios.post(
        api.addDraftDocument(id, currentAttachmentSectionId, currentAttachmentLessonId),
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      if (response.data.draft) {
        setCourse(response.data.draft);
        setTotalDocuments((prevCount) => prevCount + 1);
        swal('Success!', 'Attachment added successfully!', 'success');
        setIsAddAttachmentModalOpen(false);
        setExpandedAttachments((prev) => ({
          ...prev,
          [currentAttachmentLessonId]: {
            ...(prev[currentAttachmentLessonId] || {}),
            [response.data.draft.sections
              .find(s => s._id === currentAttachmentSectionId)
              ?.lessons.find(l => l._id === currentAttachmentLessonId)
              ?.documents.slice(-1)[0]._id]: true,
          },
        }));
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Error adding attachment:', error);
      swal('Error!', error.response?.data?.message || 'Failed to add attachment. Please try again.', 'error');
    } finally {
      setLoading(false);
      setCurrentAttachmentSectionId(null);
      setCurrentAttachmentLessonId(null);
    }
  };

  const handleDeleteAttachment = async (sectionId, lessonId, documentId) => {
    swal({
      title: 'Are you sure?',
      text: 'Once deleted, you will not be able to recover this attachment!',
      icon: 'warning',
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          setLoading(true);
          const response = await axios.delete(
            api.deleteDraftDocument(id, sectionId, lessonId, documentId),
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          setCourse(response.data.draft);
          setTotalDocuments((prevCount) => Math.max(0, prevCount - 1));
          swal('Deleted!', 'Attachment deleted successfully!', 'success');
        } catch (error) {
          swal(
            'Error!',
            'Failed to delete attachment. Please try again.',
            'error',
          );
          console.error('Error deleting attachment:', error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const getFormattedDuration = (durationInSeconds) => {
    const totalMinutes = Math.floor(durationInSeconds / 60); // Convert seconds to minutes
    const hours = Math.floor(totalMinutes / 60); // Extract whole hours
    const minutes = totalMinutes % 60; // Get remaining minutes

    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  // Update handleUpdateCourse to set draft status to true for changed fields
  const handleUpdateCourse = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      if (selectedVideo) {
        formData.append('video', selectedVideo);
        setDraftFields(prev => ({ ...prev, video: true }));
      }
      if (selectedAttachment) {
        formData.append('image', selectedAttachment);
        setDraftFields(prev => ({ ...prev, image: true }));
      }
      if (course.description) {
        formData.append('description', course.description);
        setDraftFields(prev => ({ ...prev, description: true }));
      }

      formData.append('draftFields', JSON.stringify(draftFields));

      const response = await axios.put(api.updateCourseDraft(id), formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });

      setCourse(response.data.draft);
      setSelectedVideo(null);
      setSelectedAttachment(null);
      setHasChanges(false);
      setHasSettingsChanges(false); // Disable Save Changes button after save
      // Keep draft status true for saved fields
      setDraftFields(prev => ({
        ...prev,
        video: true,
        image: true,
        description: true,
      }));
      swal('Success!', 'Course updated successfully!', 'success');
    } catch (error) {
      swal('Error!', 'Failed to update course.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (draftId) => {
    const result = await Swal.fire({
      title: 'Publish Course?',
      text: 'Once published, you cannot delete this course!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Publish it!',
    });

    if (!result.isConfirmed) return;

    try {
      // 1. Set published = true in CourseDraft
      await axios.put(api.publishDraftCourse(draftId), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 2. Copy the draft to the Course table
      const response = await axios.post(api.publishCourseFromDraft, { draftId }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data && response.data.course) {
        setCourse(response.data.course);
        Swal.fire('Success', 'Course published successfully!', 'success');
        // Optionally redirect or update UI
      } else {
        Swal.fire('Error', 'Failed to publish the course.', 'error');
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to publish the course.';
      Swal.fire('Error', message, 'error');
    }
  };

  const hasDraftChanges = (course) => {
    if (!course || !course.draftFields) return false;
    // Check for any draft fields being true
    const hasDraftFields = Object.values(course.draftFields).some(value => value === true);
    // Check for any lessons with draft: true
    const hasDraftLessons = course.sections?.some(section =>
      section.lessons?.some(lesson => lesson.draft === true),
    );
    return hasDraftFields || hasDraftLessons;
  };

  const renderDraftLabel = (fieldName, className = '') => {
    if (course?.draftFields?.[fieldName]) {
      return <span className={`draft-label ${className}`}>Draft</span>;
    }
    return null;
  };

  const handleUpdateDraftCourse = async () => {
    if (!course || !course._id) return;

    if (!hasLessons || !hasMinimumDuration) {
      swal(
        'Cannot Update Course',
        `Updated content is saved as draft. To publish updates, the course must contain at least 3 minutes of video content. Current duration: ${Math.floor(totalVideoDurationSeconds / 60)} minutes.`,
        'warning',
      );
      return;
    }

    setLoading(true);
    try {
      // Determine the draft ID to use
      // If course is published, use course.draftId, otherwise use course._id (which is the draft ID)
      const draftIdToUse = (course?.published && course?.draftId) 
        ? course.draftId 
        : course._id;
      
      const response = await axios.post(
        api.updateCourseFromDraft(),
        { draftId: draftIdToUse },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data && response.data.course) {
        setCourse(response.data.course);
        swal('Success!', 'Course updated and published from draft!', 'success');
      } else {
        swal('Error!', 'Failed to update course from draft.', 'error');
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to update course from draft.';
      swal('Error!', errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><p>Loading course details...</p></div>;
  }

  if (!course) {
    return <div className="loading-container"><p>Course details not available..</p></div>;
  }

  // Helper for section/lesson draft label
  const renderSectionLessonDraftLabel = (isDraft, className = '') => {
    if (isDraft) {
      return <span className={`draft-label ${className}`} style={{ top: 0, right: '-60px', position: 'relative' }}>Draft</span>;
    }
    return null;
  };

  // Helpers for top bar buttons
  const isIntroDraft = course?.draftFields?.video || course?.draftFields?.image || course?.draftFields?.description;
  const isOverviewDraft = course?.draftFields?.detailedDescription || course?.draftFields?.requirements || course?.draftFields?.youLearner || course?.draftFields?.targetAudience || course?.draftFields?.language;
  const isPriceDraft = course?.draftFields?.actualPrice || course?.draftFields?.offerPrice || course?.draftFields?.currency || course?.draftFields?.isFree;

  // Helper function to check if course has any lessons
  const hasLessons = course?.sections?.some(section =>
    section.lessons && section.lessons.length > 0,
  );

  // Calculate total video duration in seconds from all lessons
  const calculateTotalVideoDuration = () => {
    if (!course?.sections) return 0;
    return course.sections.reduce((total, section) => {
      if (!section.lessons) return total;
      return total + section.lessons.reduce((sectionTotal, lesson) => {
        return sectionTotal + (lesson.duration || 0);
      }, 0);
    }, 0);
  };

  const totalVideoDurationSeconds = calculateTotalVideoDuration();
  const MINIMUM_DURATION_SECONDS = 180; // 3 minutes = 180 seconds
  const hasMinimumDuration = totalVideoDurationSeconds >= MINIMUM_DURATION_SECONDS;
  
  // Course can be published only if it has lessons AND minimum 3 minutes of video
  const canPublish = hasLessons && hasMinimumDuration;

  const renderButtonDraftLabel = (show, className = '') => show ? (
    <span className={`draft-label button-draft-label ${className}`}>Draft</span>
  ) : null;

  return (
    <div>
      <InstaTopbar />
      <div className="course-details-container">
        <button
          className="close-course-details-btn"
          onClick={() => navigate(-1)}
        >
          &times;
        </button>
        <h1 className="course-titles">{course.title || 'Untitled Course'}</h1>

        <div className="btn-icon">
          <button onClick={() => setIsAddSectionModalOpen(true)} disabled={loading} className="add-section-btn">
            + Add Section
          </button>

          <button className="settings-toggle-btn" onClick={() => setIsSettingsOpen(!isSettingsOpen)}>
            <FontAwesomeIcon icon={isSettingsOpen ? faTimes : faCog} className="settings-icon" /> Intro
            {renderButtonDraftLabel(isIntroDraft, 'draft-label-intro-btn')}
          </button>

          <button className="toggle-overview-btn" onClick={() => setIsOverviewOpen(!isOverviewOpen)}>
            <FontAwesomeIcon icon={isOverviewOpen ? faTimes : faClock} className="overview-icon" /> Overview
            {renderButtonDraftLabel(isOverviewDraft, 'draft-label-overview-btn')}
          </button>
          <button className="price-info-btn" onClick={() => handleOpenPaymentModal(course._id)}>
            <FaMoneyBillAlt style={{ marginRight: '8px' }} />
            Price
            {renderButtonDraftLabel(isPriceDraft, 'draft-label-price-btn')}
          </button>
          {!course.published && (
            <button
              className={`handle-publish-btn ${!canPublish ? 'disabled' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handlePublish(course._id);
              }}
              disabled={!canPublish}
              title={
                !hasLessons 
                  ? 'Add at least one lesson to publish the course' 
                  : !hasMinimumDuration 
                    ? `Course must have at least 3 minutes of video content. Current duration: ${Math.floor(totalVideoDurationSeconds / 60)} minutes`
                    : 'Publish course'
              }
            >
              <FaUpload style={{ marginRight: '8px' }} />
              Publish
            </button>
          )}
          {course.published && (
            <>
              <button
                className={`update-course-btn ${hasDraftChanges(course) && hasMinimumDuration ? '' : 'disabled'}`}
                onClick={handleUpdateDraftCourse}
                disabled={!hasDraftChanges(course) || !hasMinimumDuration}
                title={
                  !hasDraftChanges(course)
                    ? 'Make changes to enable update'
                    : !hasMinimumDuration
                      ? `Course must have at least 3 minutes of video content to publish updates. Current duration: ${Math.floor(totalVideoDurationSeconds / 60)} minutes.`
                      : 'Update course'
                }
              >
                <FontAwesomeIcon icon={faUpload} className="update-icon" /> Update Course
              </button>
              {!hasDraftChanges(course) && (
                <div className="update-course-note">
                  <p>Make any necessary changes and click the 'Update Course' button to make the updated course visible to students.</p>
                </div>
              )}
              {hasDraftChanges(course) && !hasMinimumDuration && (
                <div className="update-course-note">
                  <p>Updated content is saved as draft. Add at least 3 minutes of total video content to publish updates.</p>
                </div>
              )}
            </>
          )}

        </div>
        {showPaymentModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="button-container">
                <button className="add-pay-cancel-btn" onClick={handleClosePaymentModal}>×</button>
              </div>
              <h3 className="price-info">Price Info</h3>
              <label>Currency:</label>
              <select
                value={paymentDetails.currency}
                onChange={(e) =>
                  setPaymentDetails({ ...paymentDetails, currency: e.target.value })
                }
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
              <div className="checkbox-container">
                <input
                  className="isFree-checkbox"
                  type="checkbox"
                  checked={effectiveIsFree}
                  disabled={PLATFORM_TEMPORARY_FREE_COURSES}
                  readOnly={PLATFORM_TEMPORARY_FREE_COURSES}
                  onChange={(e) => {
                    if (PLATFORM_TEMPORARY_FREE_COURSES) return;
                    setIsFree(e.target.checked);
                  }}
                />
                <span className="isFree-span">This is a Free Course (Offer Price set to 0)</span>
              </div>
              {!PLATFORM_TEMPORARY_FREE_COURSES && (
                <>
                  <label>Actual Price:</label>
                  <input
                    type="number"
                    placeholder="Enter actual price"
                    value={paymentDetails.actualPrice}
                    disabled={lockPricingFields}
                    readOnly={lockPricingFields}
                    style={lockPricingFields ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                    onChange={(e) => {
                      if (lockPricingFields) return;
                      setPaymentDetails({ ...paymentDetails, actualPrice: e.target.value });
                    }}
                  />
                  <label>Offer Price:</label>
                  <input
                    type="number"
                    placeholder="Enter offer price"
                    value={effectiveIsFree ? 0 : paymentDetails.offerPrice}
                    disabled={lockPricingFields}
                    readOnly={lockPricingFields}
                    style={lockPricingFields ? { opacity: 0.6, cursor: 'not-allowed' } : undefined}
                    onChange={(e) => {
                      if (lockPricingFields) return;
                      setPaymentDetails({ ...paymentDetails, offerPrice: e.target.value });
                    }}
                  />
                </>
              )}
              {PLATFORM_TEMPORARY_FREE_COURSES && (
                <p
                  style={{
                    marginTop: 10,
                    fontSize: 12,
                    color: '#b91c1c',
                    backgroundColor: '#fee2e2',
                    border: '1px solid #fecaca',
                    borderRadius: 8,
                    padding: '8px 10px',
                    fontWeight: 700,
                  }}
                >
                  Note: During the temporary period, all courses are published as <strong>FREE</strong>. Pricing is locked by the platform.
                </p>
              )}
              <div className="insta-modal-buttons">
                {!PLATFORM_TEMPORARY_FREE_COURSES && (
                  <button className="SetDefault-Price-btn" onClick={handleSetDefaultPrice}>
                    <FaHistory /> Reset to Default Price
                  </button>
                )}
                <button className="save-btn" onClick={handleSubmitPayment} disabled={!hasChanges}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Toggleable Course Overview */}
        {isOverviewOpen && (
          <div className="modal-overlay">
            <div className="course-edit-overview">
              <button
                className="in-modal-close-btn"
                onClick={() => setIsOverviewOpen(false)}
              >
                &times;
              </button>
              <div className="course-summary">
                {editOverview ? (
                  <form onSubmit={handleSaveOverview}>
                    <div className="overview-form">
                      <div className="form-group">
                        <label>Detailed Description</label>
                        <button className="in-modal-close-btn" onClick={() => setEditOverview(false)}>
                          ×
                        </button>
                        <textarea
                          value={overviewFields.detailedDescription}
                          onChange={e => handleOverviewFieldChange('detailedDescription', e.target.value)}
                          className="overview-textarea"
                        />
                        {renderDraftLabel('detailedDescription', 'draft-label-detailedDescription')}
                      </div>
                      <div className="form-group">
                        <label>Requirements</label>
                        {overviewFields.requirements.map((req, idx) => (
                          <div key={idx} className="input-with-buttons">
                            <input
                              type="text"
                              value={req}
                              onChange={e => handleOverviewFieldChange('requirements', [...overviewFields.requirements.filter((_, i) => i !== idx), e.target.value])}
                              className="overview-input"
                            />
                            <div className="input-buttons">
                              {idx === overviewFields.requirements.length - 1 && (
                                <button type="button" className="add-btns" onClick={() => handleOverviewFieldChange('requirements', [...overviewFields.requirements, ''])}>+</button>
                              )}
                              {overviewFields.requirements.length > 1 && (
                                <button type="button" className="remove-btns" onClick={() => handleOverviewFieldChange('requirements', overviewFields.requirements.filter((_, i) => i !== idx))}>-</button>
                              )}
                            </div>
                          </div>
                        ))}
                        {renderDraftLabel('requirements', 'draft-label-requirements')}
                      </div>
                      <div className="form-group">
                        <label>What you'll learn</label>
                        {overviewFields.youLearner.map((item, idx) => (
                          <div key={idx} className="input-with-buttons">
                            <input
                              type="text"
                              value={item}
                              onChange={e => handleOverviewFieldChange('youLearner', [...overviewFields.youLearner.filter((_, i) => i !== idx), e.target.value])}
                              className="overview-input"
                            />
                            <div className="input-buttons">
                              {idx === overviewFields.youLearner.length - 1 && (
                                <button type="button" className="add-btns" onClick={() => handleOverviewFieldChange('youLearner', [...overviewFields.youLearner, ''])}>+</button>
                              )}
                              {overviewFields.youLearner.length > 1 && (
                                <button type="button" className="remove-btns" onClick={() => handleOverviewFieldChange('youLearner', overviewFields.youLearner.filter((_, i) => i !== idx))}>-</button>
                              )}
                            </div>
                          </div>
                        ))}
                        {renderDraftLabel('youLearner', 'draft-label-youLearner')}
                      </div>
                      <div className="form-group">
                        <label>Target Audience</label>
                        {overviewFields.targetAudience.map((aud, idx) => (
                          <div key={idx} className="input-with-buttons">
                            <input
                              type="text"
                              value={aud}
                              onChange={e => handleOverviewFieldChange('targetAudience', [...overviewFields.targetAudience.filter((_, i) => i !== idx), e.target.value])}
                              className="overview-input"
                            />
                            <div className="input-buttons">
                              {idx === overviewFields.targetAudience.length - 1 && (
                                <button type="button" className="add-btns" onClick={() => handleOverviewFieldChange('targetAudience', [...overviewFields.targetAudience, ''])}>+</button>
                              )}
                              {overviewFields.targetAudience.length > 1 && (
                                <button type="button" className="remove-btns" onClick={() => handleOverviewFieldChange('targetAudience', overviewFields.targetAudience.filter((_, i) => i !== idx))}>-</button>
                              )}
                            </div>
                          </div>
                        ))}
                        {renderDraftLabel('targetAudience', 'draft-label-targetAudience')}
                      </div>
                      <div className="form-group">
                        <label>Language</label>
                        <input
                          type="text"
                          value={overviewFields.language}
                          onChange={e => handleOverviewFieldChange('language', e.target.value)}
                          className="overview-input"
                        />
                        {renderDraftLabel('language', 'draft-label-language')}
                      </div>
                      <div className="overview-buttons">
                        <button type="submit" className="save-overview-btn" disabled={!hasOverviewChanges}>
                          {loading ? 'Saving...' : 'Save Overview'}
                        </button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <div className="overview-display">
                    <button className="edit-overview-btn" onClick={() => setEditOverview(true)}>
                      Edit Overview
                    </button>
                    <div className="overview-section">

                      <h3>Detailed Description</h3>
                      <p>{course.detailedDescription || 'N/A'}</p>
                    </div>

                    <div className="overview-section">
                      <h3>Requirements</h3>
                      <ul>
                        {(course.requirements || []).map((req, index) => (
                          <li key={index}>{req}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="overview-section">
                      <h3>What you'll learn</h3>
                      <ul>
                        {(course.youLearner || []).map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="overview-section">
                      <h3>Target Audience</h3>
                      <ul>
                        {(course.targetAudience || []).map((aud, index) => (
                          <li key={index}>{aud}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="overview-section">
                      <h3>Language</h3>
                      <p>{course.language || 'N/A'}</p>
                    </div>


                  </div>
                )}
              </div>
            </div>
          </div>
        )}


        {isSettingsOpen && (
          <div className="modal-overlay" >
            <div className="course-title-img-vid">
              <div className="media-container">
                <button
                  className="in-modal-close-btn"
                  onClick={() => {
                    setIsSettingsOpen(false);
                    // Reset changes when closing
                    setHasSettingsChanges(false);
                    setSelectedVideo(null);
                    setSelectedAttachment(null);
                    if (course) {
                      setCourse({
                        ...course,
                        description: originalSettings.description,
                      });
                    }
                  }}
                >
                  &times;
                </button>

                <div className="image-edit-container">
                  <label>Course Thumbnail</label>
                  <div className="edit-btn-container">
                    <button
                      className="edit-btn"
                      onClick={() => document.getElementById('image-input').click()}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                  </div>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={`${api.img + course.image}`}
                      alt="Thumbnail"
                      className="course-thumbnail-img"
                      onClick={() => openModal({
                        type: 'image',
                        src: `${api.img + course.image}`,
                      })}
                    />
                    {renderDraftLabel('image', 'draft-label-image')}
                  </div>
                  <input
                    type="file"
                    id="image-input"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      setSelectedAttachment(e.target.files[0]);
                      setHasSettingsChanges(true);
                    }}
                  />
                </div>

                <div className="video-edit-container">
                  <label>Introduction Video</label>
                  <div className="edit-btn-container">
                    <button
                      className="edit-btn"
                      onClick={() => document.getElementById('video-input').click()}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                  </div>
                  {course.video && (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <video
                        className="course-introduction-vid"
                        onClick={() => openModal({
                          type: 'video',
                          src: `${api.img + course.video}`,
                        })}
                      >
                        <source src={`${api.img + course.video}`} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                      {renderDraftLabel('video', 'draft-label-video')}
                    </div>
                  )}
                  <input
                    type="file"
                    id="video-input"
                    accept="video/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;

                      // Check file type (MP4 is recommended - supports all devices, browsers, OS, and players)
                      const validTypes = ['video/mp4', 'video/webm', 'video/avi', 'video/quicktime', 'video/x-msvideo'];
                      const validExtensions = ['.mp4', '.webm', '.avi', '.mov'];
                      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
                      const isValidFormat = validTypes.includes(file.type) || validExtensions.includes(fileExtension);
                      
                      if (!file.type.startsWith('video/') || !isValidFormat) {
                        swal('Error!', 'Please upload a valid video file (MP4, AVI, MOV, WebM). MP4 is recommended for best compatibility.', 'error');
                        e.target.value = '';
                        return;
                      }

                      // Check file size (500MB limit for introduction video)
                      const MAX_INTRO_VIDEO_SIZE = 500 * 1024 * 1024; // 500MB
                      if (file.size > MAX_INTRO_VIDEO_SIZE) {
                        swal('Error!', 'Introduction video size exceeds the maximum limit of 500MB. Please compress the video or use a smaller file.', 'error');
                        e.target.value = '';
                        return;
                      }

                      // Validate duration (5 minutes max)
                      try {
                        const video = document.createElement('video');
                        video.preload = 'metadata';
                        const duration = await new Promise((resolve, reject) => {
                          video.onloadedmetadata = () => {
                            window.URL.revokeObjectURL(video.src);
                            resolve(video.duration);
                          };
                          video.onerror = reject;
                          video.src = URL.createObjectURL(file);
                        });

                        const MAX_INTRO_VIDEO_SECONDS = 300; // 5 minutes = 300 seconds
                        if (duration > MAX_INTRO_VIDEO_SECONDS) {
                          swal('Error!', 'Introduction video must be 5 minutes or less. Maximum duration: 5 minutes.', 'error');
                          e.target.value = '';
                          return;
                        }

                        // If validation passes, set the video
                        setSelectedVideo(file);
                      setHasSettingsChanges(true);
                      } catch (error) {
                        swal('Error!', 'Could not verify video duration. Please try another video.', 'error');
                        e.target.value = '';
                      }
                    }}
                  />
                </div>
              </div>

              <div className="description-form">
                <label>Course Description   {renderDraftLabel('description', 'draft-label-description')}</label>

                <textarea
                  className="ta"
                  value={course.description || 'No description available'}
                  onChange={(e) => handleSettingsChange('description', e.target.value)}
                  placeholder="Enter course description..."
                />

                <div className="sub-btn">
                  <button
                    className="sav"
                    onClick={async () => {
                      await handleUpdateCourse();
                      setIsSettingsOpen(false); // Close the modal after saving
                    }}
                    disabled={loading || !hasSettingsChanges}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Section Modal */}
        <AddSectionModal
          isOpen={isAddSectionModalOpen}
          onClose={() => setIsAddSectionModalOpen(false)}
          onSave={handleAddSection}
          loading={loading}
        />

        {course?.sections?.length > 0 ? (
          <>
            {!canPublish && !course.published && (
              <div className="publish-requirements-message">
                <p>Once a course has been published, it cannot be deleted; however, it can still be edited.</p>
                <br />
                {!hasLessons ? (
                  <p>Please add at least one lesson. Minimum 3 minutes video is required to publish the course.</p>
                ) : !hasMinimumDuration ? (
                  <p>Course must have at least 3 minutes of video content. Current duration: {Math.floor(totalVideoDurationSeconds / 60)} minutes.</p>
                ) : null}
                <br />
                <p>Thank you for your understanding.</p>
              </div>
            )}
            {course.sections.map((section, sectionIndex) => {
              const totalDuration = section.lessons.reduce((acc, lesson) => acc + (lesson.duration || 0), 0);
              return (
                <div key={section._id} className="section-container">
                  <div
                    className="section-header"
                    onClick={() =>
                      setExpandedSectionId((prevId) =>
                        prevId === section._id ? null : section._id,
                      )
                    }
                  >
                    <div className="section-title-container">
                      <h3 className="section-title">
                        <span className="section-collapse-icon">
                          {expandedSectionId === section._id ? '▾' : '▸'}
                        </span>
                        <span>
                          Section {sectionIndex + 1} - {section.name}
                          {renderSectionLessonDraftLabel(section.draft, 'draft-label-section')}
                        </span>
                      </h3>
                      <div className="section-meta">
                        <span className="lecture-count">
                          {section.lessons.length} lectures
                        </span>
                        <span className="duration-dot">•</span>
                        <span className="total-duration">
                          <FontAwesomeIcon
                            icon={faClock}
                            className="duration-icon"
                          />
                          {getFormattedDuration(totalDuration)}
                        </span>
                      </div>
                    </div>
                    <div className="section-controls">
                      <button
                        className="control-btn add-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentSectionId(section._id);
                          setIsAddLessonModalOpen(true);
                        }}
                        title="Add lesson"
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                      <button
                        className="control-btn edit-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentSectionId(section._id);
                          setCurrentSectionName(section.name);
                          setIsEditSectionModalOpen(true);
                        }}
                        title="Edit section"
                      >
                        <FontAwesomeIcon icon={faPen} />
                      </button>
                      <button
                        className="control-btn delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSection(section._id);
                        }}
                        title="Delete section"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>

                  {expandedSectionId === section._id && (
                    <div className="section-content">
                      {section.lessons.map((lesson, lessonIndex) => (
                        <div key={lesson._id} className="lesson-container">
                          <div
                            className="lesson-header"
                            onClick={() => handleLessonClick(lesson._id)}
                          >
                            <div className="lesson-info">
                              <div className="lesson-title">
                                <strong>Lesson {lessonIndex + 1}:</strong>{' '}
                                {lesson.name}
                                {renderSectionLessonDraftLabel(lesson.draft, 'draft-label-lesson')}
                              </div>
                              <div className="lesson-meta">
                                <span className="duration">
                                  <FontAwesomeIcon



                                    icon={faClock}
                                    className="duration-icon"
                                  />
                                  {getFormattedDuration(lesson.duration || 0)}
                                </span>
                                {lesson.documents.length > 0 && (
                                  <>
                                    <span className="meta-dot">•</span>
                                    <span className="attachment-count">
                                      <FontAwesomeIcon
                                        icon={faPaperclip}
                                        className="attachment-icon"
                                      />
                                      {lesson.documents.length}{' '}
                                      {lesson.documents.length === 1
                                        ? 'attachment'
                                        : 'attachments'}
                                    </span>
                                  </>
                                )}
                              </div>

                            </div>
                            <div className="lesson-controls">
                              <button
                                className="control-btn edit-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentLessonId(lesson._id);
                                  setCurrentLessonName(lesson.name);
                                  setCurrentSectionId(section._id);
                                  setIsEditLessonModalOpen(true);
                                }}
                                title="Edit lesson"
                              >
                                <FontAwesomeIcon icon={faPen} />
                              </button>
                              <button
                                className="control-btn delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteLesson(section._id, lesson._id);
                                }}
                                title="Delete lesson"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                              <button
                                className="control-btn attachment-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentAttachmentSectionId(section._id);
                                  setCurrentAttachmentLessonId(lesson._id);
                                  setIsAddAttachmentModalOpen(true);
                                }}
                                title="Add attachment"
                              >
                                <FontAwesomeIcon icon={faPaperclip} />
                              </button>
                              {/* Preview Checkbox */}
                              <label
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginLeft: 8,
                                  cursor: 'pointer',
                                }}
                              >
                                <button
                                  className="control-btn checkbox-enable-btn"
                                  title="Enabel to allow previewing this lesson to students without enrolling in the course"
                                >
                                  <input
                                    type="checkbox"
                                    checked={lesson.preview || false}
                                    onChange={async (e) => {
                                      const checked = e.target.checked;
                                      setLoading(true);
                                      try {
                                        const formData = new FormData();
                                        formData.append('lessonName', lesson.name);
                                        formData.append('preview', checked);
                                        // Only send video if editing video, not for preview toggle
                                        const response = await axios.put(
                                          api.editDraftLesson(
                                            id,
                                            section._id,
                                            lesson._id,
                                          ),
                                          formData,
                                          {
                                            headers: {
                                              Authorization: `Bearer ${token}`,
                                              'Content-Type': 'multipart/form-data',
                                            },
                                          },
                                        );
                                        if (response.data.draft) {
                                          setCourse(response.data.draft);
                                        }
                                      } catch (error) {
                                        swal(
                                          'Error!',
                                          'Failed to update preview status.',
                                          'error',
                                        );
                                      } finally {
                                        setLoading(false);
                                      }
                                    }}
                                    disabled={loading}
                                    style={{ marginRight: 4 }}
                                  />

                                </button>
                              </label>

                              <button
                                className="control-btn video-toggle-btn"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  const willOpen = !expandedVideos[lesson._id];
                                  if (willOpen) {
                                    setExpandedLessonId(lesson._id); // Ensure lesson is expanded
                                    toggleVideo(lesson._id);
                                    // Wait for DOM update, then play
                                    setTimeout(() => {
                                      const vid = videoRefs.current[lesson._id];
                                      if (vid) {
                                        vid.play().catch(() => { });
                                      }
                                    }, 300);
                                  } else {
                                    toggleVideo(lesson._id);
                                  }
                                }}
                                title={
                                  expandedVideos[lesson._id]
                                    ? 'Close video'
                                    : 'Play video'
                                }
                              >
                                <FontAwesomeIcon
                                  icon={
                                    expandedVideos[lesson._id] ? faTimes : faPlay
                                  }
                                />
                              </button>

                            </div>
                          </div>

                          {expandedLessonId === lesson._id && (
                            <div className="lesson-content">
                              {lesson.video && expandedVideos[lesson._id] && (
                                <div className="video-container">
                                  <button
                                    className="video-close-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleVideo(lesson._id);
                                    }}
                                    title="Close video"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                  <video
                                    ref={(el) =>
                                      (videoRefs.current[lesson._id] = el)
                                    }
                                    controls
                                    className="lesson-video"
                                  >
                                    <source
                                      src={`${api.img}${lesson.video}`}
                                      type="video/mp4"
                                    />
                                    Your browser does not support the video tag.
                                  </video>
                                </div>
                              )}
                              {lesson.video && !expandedVideos[lesson._id] && (
                                <div
                                  className="video-thumbnail"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleVideo(lesson._id);
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faPlay}
                                    className="play-icon"
                                  />
                                  <button type="button" className="play-button">
                                    Play Video
                                  </button>
                                </div>
                              )}

                              {lesson.documents.length > 0 && (
                                <div className="attachments-section">
                                  <h4 className="attachments-title">
                                    <FontAwesomeIcon icon={faPaperclip} />{' '}
                                    Attachments
                                  </h4>
                                  {lesson.documents.map((doc) => (
                                    <div
                                      key={doc._id}
                                      className="attachment-container"
                                    >
                                      <div
                                        className="attachment-header"
                                        onClick={() =>
                                          toggleAttachment(lesson._id, doc._id)
                                        }
                                      >
                                        <span className="attachment-name">
                                          <FontAwesomeIcon
                                            icon={
                                              expandedAttachments[lesson._id]?.[
                                                doc._id
                                              ]
                                                ? faChevronDown
                                                : faChevronRight
                                            }
                                            className="attachment-arrow"
                                          />
                                          {doc.name}
                                        </span>
                                      </div>
                                      {expandedAttachments[lesson._id]?.[
                                        doc._id
                                      ] && (
                                        <div className="attachment-actions">
                                          <a
                                            href={`${api.img}${doc.file}`}
                                            download
                                            className="attachment-download"
                                            title="Download attachment"
                                          >
                                            <FontAwesomeIcon icon={faDownload} />{' '}
                                              Download
                                          </a>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleDeleteAttachment(
                                                section._id,
                                                lesson._id,
                                                doc._id,
                                              );
                                            }}
                                            className="attachment-delete"
                                            title="Delete attachment"
                                            disabled={loading}
                                          >
                                            <FontAwesomeIcon icon={faTrash} />{' '}
                                              Delete
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <div className="no-sections">
            {!canPublish && !course.published && (
              <div className="publish-requirements-message">
                {!hasLessons ? (
                  <p>Please add at least one lesson. Minimum 3 minutes video is required to publish the course.</p>
                ) : !hasMinimumDuration ? (
                  <p>Course must have at least 3 minutes of video content. Current duration: {Math.floor(totalVideoDurationSeconds / 60)} minutes.</p>
                ) : (
                <p>Please add lessons and fix the price before publishing the course.</p>
                )}
              </div>
            )}
            <p>No sections available. Click "Add Section" to create your first section.</p>
          </div>
        )}

        {/* Add Lesson Modal */}
        <AddLessonModal
          isOpen={isAddLessonModalOpen}
          onClose={() => setIsAddLessonModalOpen(false)}
          onSave={handleAddLesson}
          loading={loading}
        />
        <EditSectionModal
          isOpen={isEditSectionModalOpen}
          onClose={() => setIsEditSectionModalOpen(false)}
          onSave={handleEditSection}
          sectionName={currentSectionName}
          loading={loading}
        />
        <EditLessonModal
          isOpen={isEditLessonModalOpen}
          onClose={() => setIsEditLessonModalOpen(false)}
          onSave={(editType, editedName, editedVideo) =>
            handleEditLesson(currentSectionId, currentLessonId, editType, editedName, editedVideo)
          }
          lessonName={currentLessonName}
          loading={loading}
        />
        {/* AddAttachmentModal rendered once at the root */}
        <AddAttachmentModal
          isOpen={isAddAttachmentModalOpen}
          onClose={() => setIsAddAttachmentModalOpen(false)}
          onSave={handleAddAttachment}
          loading={loading}
        />

        {/* <EditLessonModal
            isOpen={isEditLessonModalOpen}
            onClose={() => setIsEditLessonModalOpen(false)}
            onSave={(editedName) =>
              handleEditLesson(currentSectionId, currentLessonId, editedName)
            }
            lessonName={currentLessonName}
            loading={loading}
          /> */}

        {/* Add the fixed add section button at the bottom of the return statement, just before the closing div */}
        <button
          className="fixed-add-section-btn"
          onClick={() => setIsAddSectionModalOpen(true)}
          title="Add new section"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>

      <MediaModal
        isOpen={isModalOpen}
        onClose={closeModal}
        content={modalContent}
      />
    </div>
  );

};

export default InstructorCourseDetails;
